import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, DatePicker, Descriptions, Form, Input, InputNumber, Space, Spin, Table } from "antd";
import { DeleteOutline } from "antd-mobile-icons";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../common/api";
import { requiredField, toLocaleString } from "../common/common";
import DebounceSelect from "../common/DebounceSelect";

const calcAmounts = (amount, n) => {
    if (n === 0) return [];
    const part = Math.floor((amount / n) * 100, 2) / 100;
    const rest = Math.floor((amount - part * (n - 1)) * 100) / 100;
    return [...[...Array(n - 1).keys()].map((x) => part), rest];
};

export default function () {
    const { id } = useParams();
    const [form] = Form.useForm();
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const chargesWatch = Form.useWatch("charges", form);

    useEffect(() => {
        setLoading(true);
        axiosInstance
            .get(`fine/${id}`)
            .then(({ data }) => {
                setData(data);
                const fineAmount =
                    data.amountPaid == "REDUCED" ? data.reducedAmount : data.fullAmount;
                const amounts = calcAmounts(fineAmount, data.employees.length);
                form.setFieldValue(
                    "charges",
                    (data.employees ?? []).map((x, index) => ({
                        employee: { ...x, label: x.fullName, value: x.id, key: x.id },
                        amount: amounts[index],
                    })),
                );
            })
            .finally(() => setLoading(false));
    }, []);

    const fineAmount = data.amountPaid == "REDUCED" ? data.reducedAmount : data.fullAmount;
    const onFinish = (form) => {
        setLoading(true);
        axiosInstance
            .post(`fine/${id}/charge`, {
                ...form,
                charges: form.charges.map((x) => ({
                    employee: x.employee.value,
                    amount: x.amount,
                    date: x.date.format("YYYY-MM-DD")
                })),
            })
            .then(() => navigate(-1))
            .finally(() => setLoading(false));
    };
    const calcCharges = () => {
        const fineAmount = data.amountPaid == "REDUCED" ? data.reducedAmount : data.fullAmount;
        const amounts = calcAmounts(fineAmount, chargesWatch.length);
        form.setFieldValue(
            "charges",
            chargesWatch.map((x, index) => ({
                ...x,
                amount: amounts[index],
            })),
        );
    };

    return (
        <Spin spinning={loading}>
            <Descriptions layout="vertical" size="small">
                <Descriptions.Item label="Importo da addebitare">
                    {toLocaleString(fineAmount ?? 0)}
                </Descriptions.Item>
            </Descriptions>
            <Form form={form} style={{ marginTop: 30 }} onFinish={onFinish}>
                <Form.List name={"charges"}>
                    {(fields, { add, remove }) => (
                        <div style={{ display: "flex", flexDirection: "column", rowGap: 16 }}>
                            <Space>
                                <Button icon={<PlusOutlined />} onClick={() => add()}>
                                    Nuova riga
                                </Button>
                                <Button onClick={calcCharges}>Ricalcola importi</Button>
                            </Space>
                            <Table
                                size="small"
                                tableLayout="fixed"
                                bordered
                                summary={() => (
                                    <Table.Summary fixed="bottom">
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={2}>
                                                Totale
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={2}>
                                                <div style={{ marginLeft: 13, fontWeight: "bold" }}>
                                                    {toLocaleString(
                                                        (chargesWatch ?? []).reduce(
                                                            (acc, prev) =>
                                                                acc + (prev?.amount ?? 0),
                                                            0,
                                                        ),
                                                    )}
                                                </div>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={2}></Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                )}
                                columns={[
                                    {
                                        title: "Nominativo",
                                        key: "employee",
                                        render: (field) => (
                                            <Form.Item
                                                name={[field.name, "employee"]}
                                                rules={[requiredField]}
                                                style={{ margin: 0 }}
                                            >
                                                <DebounceSelect
                                                    fetchOptions={async (query) =>
                                                        axiosInstance
                                                            .get("employeeCompact", {
                                                                params: {
                                                                    startDate: "1900-01-01",
                                                                    endDate: "9999-01-01",
                                                                    query,
                                                                },
                                                            })
                                                            .then(({ data }) =>
                                                                data.map((x) => ({
                                                                    label: x.fullName,
                                                                    value: x.id,
                                                                })),
                                                            )
                                                    }
                                                />
                                            </Form.Item>
                                        ),
                                    },
                                    {
                                        title: "Data",
                                        width:'20%',
                                        key: "date",
                                        render: (field) => (
                                            <Form.Item
                                                rules={[requiredField]}
                                                name={[field.name, "date"]}
                                                style={{ margin: 0, width: "100%" }}
                                            >
                                            <DatePicker format="DD-MM-YYYY" style={{width:'100%'}}/>
                                            </Form.Item>
                                        ),
                                    },
                                    {
                                        title: "Importo",
                                        width:'15%',
                                        key: "amount",
                                        render: (field) => (
                                            <Form.Item
                                                rules={[requiredField]}
                                                name={[field.name, "amount"]}
                                                style={{ margin: 0, width: "100%" }}
                                            >
                                                <InputNumber
                                                    decimalSeparator=","
                                                    precision={2}
                                                    controls={false}
                                                    style={{ width: "100%" }}
                                                />
                                            </Form.Item>
                                        ),
                                    },
                                    {
                                        title: "",
                                        key: "action",
                                        width: 50,
                                        render: (field) => (
                                            <Button
                                                color="danger"
                                                variant="text"
                                                onClick={() => remove(field.name)}
                                                icon={<DeleteOutlined />}
                                            />
                                        ),
                                    },
                                ]}
                                pagination={false}
                                scroll={{ y: 500 }}
                                dataSource={fields}
                            />
                        </div>
                    )}
                </Form.List>
                <Form.Item style={{ textAlign: "end", marginTop: 32 }}>
                    <Button htmlType="submit" type="primary">
                        Conferma
                    </Button>
                </Form.Item>
            </Form>
        </Spin>
    );
}
